import {Link, useLocation} from "react-router-dom";
import { MenuItems } from "../../constants/index.js";
import React, { useState, useMemo, useCallback } from "react";
import { useLanguage } from "../../utils/languageContext.js";
import { getTranslation } from "../../utils/translate.js";
import { useSidebar } from "../../utils/sidebarContext.js";
import { useAuth } from "../../utils/authContext.js";

// Pre-compute translation map for better performance
const getTranslationMap = (language) => {
    return {
        'Dashboard': getTranslation('menuItems.dashboard', language),
        'Orders': getTranslation('menuItems.orders', language),
        'OrderBoard': getTranslation('menuItems.orderBoard', language),
        'Chat': getTranslation('menuItems.chat', language),
        'Menu': getTranslation('menuItems.menu', language),
        'Schedule': getTranslation('menuItems.schedule', language),
        'Profile': getTranslation('menuItems.profile', language),
        'Payments': getTranslation('menuItems.payments', language),
        'Promo': getTranslation('menuItems.promocodes', language),
        'Clients': getTranslation('menuItems.clients', language),
        'Delivery': getTranslation('menuItems.delivery', language),
        'Configurations': getTranslation('menuItems.configurations', language),
        'Integrations': getTranslation('menuItems.integrations', language),
    };
};

const Menu = React.memo(({ onItemClick, isModal }) => {
    const location = useLocation();
    const [hoveredItem, setHoveredItem] = useState(null);
    const { language } = useLanguage();
    const { isCollapsed } = useSidebar();
    const { chefData } = useAuth();
    const hasTelegramBot = chefData?.hasTelegramBot === true;
    
    const shouldShowCollapsed = useMemo(() => !isModal && isCollapsed, [isModal, isCollapsed]);
    
    const handleItemClick = useCallback((item) => {
        if (onItemClick) {
            onItemClick();
        }
    }, [onItemClick]);

    const handleMouseEnter = useCallback((key) => {
        setHoveredItem(key);
    }, []);

    const handleMouseLeave = useCallback(() => {
        setHoveredItem(null);
    }, []);

    const translationMap = useMemo(() => getTranslationMap(language), [language]);

    const navClassName = useMemo(() => 
        `flex-1 space-y-4 pt-6 ${!isModal ? 'border-t border-gray-200 dark:border-gray-700' : ''}`,
        [isModal]
    );

    const menuItems = useMemo(() =>
        MenuItems.map((item) => {
            if (item.key === 'Payments') {
                const path = hasTelegramBot ? '/telegram-payments' : '/payments';
                return {
                    ...item,
                    path,
                    label: translationMap[item.key] || item.key,
                    isActive: location.pathname === path,
                };
            }
            return {
                ...item,
                label: translationMap[item.key] || item.key,
                isActive: location.pathname === item.path,
            };
        }),
        [location.pathname, translationMap, hasTelegramBot]
    );

    return (
        <div className="flex flex-col flex-1">
            <nav className={navClassName}>
                {menuItems.map(item => {
                    const { label, isActive } = item;
                    return (
                        <div key={item.key} className="relative group">
                            <Link
                                onClick={() => handleItemClick(item)}
                                to={item.path}
                                className={`flex items-center rounded-lg font-semibold text-base
                                ${shouldShowCollapsed ? 'mx-2 px-2 py-2 justify-center' : 'mx-3 px-3 py-2'}
                                ${isActive
                                    ? 'bg-gray-100 dark:bg-[#1c2128] text-gray-900 dark:text-[#c9d1d9] shadow-sm'
                                    : 'text-black dark:text-[#b1bac4] hover:bg-gray-50 dark:hover:bg-[#1c2128] hover:text-gray-900 dark:hover:text-[#c9d1d9]'}
                                focus:outline-none focus:ring-2 focus:ring-gray-300 dark:focus:ring-[#30363d]`}
                                style={{
                                    transition: 'padding 150ms cubic-bezier(0.4, 0, 0.2, 1), background-color 150ms cubic-bezier(0.4, 0, 0.2, 1), color 150ms cubic-bezier(0.4, 0, 0.2, 1)',
                                    willChange: 'padding, background-color, color'
                                }}
                                onMouseEnter={() => handleMouseEnter(item.key)}
                                onMouseLeave={handleMouseLeave}
                                aria-current={isActive ? 'page' : undefined}
                            >
                                <span 
                                    className={`flex items-center justify-center shrink-0 ${hoveredItem === item.key ? 'scale-110' : ''} ${
                                        isActive 
                                            ? 'text-gray-900 dark:text-[#c9d1d9]' 
                                            : 'text-gray-600 dark:text-[#8b949e]'
                                    } ${shouldShowCollapsed ? 'w-5 h-5' : 'w-5 h-5 mr-3'}`}
                                    style={{
                                        minWidth: '20px',
                                        minHeight: '20px',
                                        transition: 'transform 120ms cubic-bezier(0.4, 0, 0.2, 1), margin-right 120ms cubic-bezier(0.4, 0, 0.2, 1)',
                                        transform: hoveredItem === item.key ? 'scale(1.1)' : 'scale(1)'
                                    }}
                                >
                                    <div className="w-5 h-5 flex items-center justify-center [&>svg]:w-full [&>svg]:h-full">
                                        {item.icon}
                                    </div>
                                </span>
                                {!shouldShowCollapsed && (
                                    <span 
                                        className="flex-1 min-w-0"
                                        style={{
                                            transition: 'opacity 120ms cubic-bezier(0.4, 0, 0.2, 1)'
                                        }}
                                    >
                                        {label}
                                    </span>
                                )}
                            </Link>
                            {shouldShowCollapsed && (
                                <div 
                                    className="absolute left-full ml-2 top-1/2 -translate-y-1/2 px-2 py-1 bg-gray-900 dark:bg-[#21262d] text-white text-sm rounded-md opacity-0 group-hover:opacity-100 pointer-events-none whitespace-nowrap shadow-lg"
                                    style={{
                                        zIndex: 9999,
                                        transition: 'opacity 100ms cubic-bezier(0.4, 0, 0.2, 1)'
                                    }}
                                >
                                    {label}
                                    <div className="absolute left-0 top-1/2 -translate-y-1/2 -translate-x-full w-0 h-0 border-t-4 border-t-transparent border-r-4 border-r-gray-900 dark:border-r-[#21262d] border-b-4 border-b-transparent"></div>
                                </div>
                            )}
                        </div>
                    );
                })}
            </nav>
        </div>
    )
});

Menu.displayName = 'Menu';

export default Menu;
